import { useState, SyntheticEvent } from "react";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { ZipCodeBarProps } from "@Types/ZipCodeBar";
import { getItem } from "@Utils/browserStorage";
import { nextAppEnv } from "@Utils/environment";
import { isCanadianLocale } from "@Utils/locale";
import { useZipCode } from "@wff/hooks/useZipCode";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";
import {
  initialStateZipcode,
  setZipCodeHandler,
  toggleZipCodePopUp,
  ZipCodeSelector,
} from "@wff/store/zipcodeSlice";
import Image from "components/Image";
import AccordionButton from "./accordionButton/AccordionButton";
import classes from "./ZipCodeBar.module.scss";

const featureToggleForClickToCollectForCA =
  nextAppEnv.feature_toggle.CA.click_and_collect === "true";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  width: "100%",
  margin: "0 auto",
  color: "#fff",
  backgroundColor: "#3d3935",
  ".Mui-expanded": {
    backgroundColor: "#3d3935",
  },
  "&:before": {
    display: "none",
  },
}));

export default function ZipCodeBar({
  labelFindStore,
  labelZip,
  labelDelivery,
  labelPickup,
  textDeliveryAvailable,
  textDeliveryNotAvailable,
  textPickupAvailable,
  textPickupNotAvailable,
  drawerTitleAvailable,
  drawerTitleNotAvailable,
  drawerMessageAvailable,
  drawerMessageNotAvailable,
  drawerButtonLabel,
  drawerUnderlinedText,
  iconFindStore,
  iconDelivery,
  availability,
}: ZipCodeBarProps) {
  const { zipcode } = useZipCode();
  const [expanded, setExpanded] = useState(false);
  const dispatch = useAppDispatch();
  const { locale } = useAppSelector((state) => state);
  const isCanadianUser = isCanadianLocale(locale);
  const { isPickupAvailable } = useAppSelector(ZipCodeSelector);

  const openZipCodePopUp = () => {
    setZipCodeHandler({
      ...initialStateZipcode,
      ...(isCanadianUser && { prevZipcode: zipcode }),
    });
    dispatch(toggleZipCodePopUp());
  };

  const showPickupForCA = isCanadianUser && featureToggleForClickToCollectForCA;

  const zipCodePickupAvailabilityInLocalStorageForCA = getItem(
    STORAGE_KEYS.CUSTOMER_ZIP_PICKUP_AVAILABILITY_CA,
    STORAGE_TYPES.LOCAL
  );

  const pickupAvailable =
    isPickupAvailable ||
    zipCodePickupAvailabilityInLocalStorageForCA === "true";

  // TODO: replace with real validation for availability of Delivery to customers Zip Code.
  const textDelivery = availability
    ? textDeliveryAvailable
    : textDeliveryNotAvailable;

  const textPickup = pickupAvailable
    ? textPickupAvailable
    : textPickupNotAvailable;

  // TODO: replace with real validation for availability of Delivery to customers Zip Code.
  const textDrawerTitle = availability
    ? drawerTitleAvailable
    : drawerTitleNotAvailable;

  // TODO: replace with real validation for availability of Delivery to customers Zip Code.
  const textDrawerMessage = availability
    ? drawerMessageAvailable
    : drawerMessageNotAvailable;

  const AccordionDetails = styled(MuiAccordionDetails)(() => ({
    backgroundColor: "#fafafa",
    color: "#3d3935",
    borderBottom: "1px solid #3d3935",
    textTransform: "none",
    overflow: "hidden",
    transitionTimingFunction: "ease",
  }));

  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary {...props} />
  ))(() => ({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    minHeight: "30px",
    maxWidth: "47.375rem",
    margin: "0 auto",
    padding: 0,
    ".MuiAccordionSummary-content": {
      margin: 0,
      minHeight: "37px",
      maxHeight: "40px",
    },
  }));

  const toggleDrawer = (event: SyntheticEvent) => {
    event.preventDefault();
    setExpanded(!expanded);
  };

  const zipcodeClass = zipcode
    ? classes["trade-bar"]
    : classes["trade-bar"] + " " + classes["trade-bar__hide"];

  return (
    <div className={zipcodeClass} onClick={toggleDrawer}>
      {zipcode && availability !== null ? ( // using availability to hold load until we have it set.
        <Accordion expanded={expanded} onChange={toggleDrawer}>
          <AccordionSummary
            aria-controls="panel_zipcode-content"
            id="panelzipcode-header"
          >
            <div className={classes["trade-bar__available"]}>
              <p className={classes["trade-bar__available-message"]}>
                {labelZip}
                <span data-testid="zipcode-bar--zipcode">{zipcode}</span>
              </p>
              <p className={classes["trade-bar__available-message"]}>
                {labelDelivery}
                <span>
                  <span
                    className="trade-bar__message-full"
                    data-testid="delivery-availability"
                  >
                    {textDelivery}
                  </span>
                </span>
              </p>

              {showPickupForCA ? (
                <p className={classes["trade-bar__available-message"]}>
                  {labelPickup}
                  <span
                    className="trade-bar__message-full"
                    data-testid="pickup-availability"
                  >
                    {textPickup}
                  </span>
                </p>
              ) : (
                <></>
              )}

              <AccordionButton onClick={toggleDrawer} active={expanded} />
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes["trade__content"]}>
            <div className={classes["trade__services"]}>
              <div className={classes["trade__service"]}>
                <div className={classes["trade__service--icon"]}>
                  <Image
                    src={`${iconDelivery?.url}?w=30&h=30`}
                    width={30}
                    height={30}
                    alt="icon_delivery"
                    data-testid="icon_delivery"
                  />
                </div>
                <p className={classes["trade__service--text"]}>
                  <strong>{textDrawerTitle}</strong>
                  <br />
                  {textDrawerMessage}
                </p>
              </div>
              {/* 
              Reference:https://wild-fork-canada.atlassian.net/browse/CE-1727
              <PickupMessage
                showPickup={showPickupForCA && pickupAvailable}
                iconPickup={iconPickup}
                pickupTitleAvailable={pickupTitleAvailable}
                nearestPickupLocation={nearestPickupLocation}
                textPickup={textPickup}
              /> */}
            </div>
            <div className={classes["trade__actions"]}>
              <button
                className="action action-primary"
                data-testid="button-open-zip-popup"
                onClick={openZipCodePopUp}
              >
                {drawerButtonLabel}
              </button>
              <div
                className={classes["trade__continue"]}
                onClick={toggleDrawer}
              >
                {drawerUnderlinedText}
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      ) : (
        !isCanadianUser && (
          <div className={classes["trade-bar__find-store"]}>
            <div className={classes["trade-bar__target"]}>
              <Image
                src={`${iconFindStore?.url}?w=30&h=30`}
                width={30}
                height={30}
                alt="icon_target"
                data-testid="icon_target"
              />
            </div>
            <div className={classes["trade-bar__find-store-message"]}>
              {labelFindStore}
            </div>
          </div>
        )
      )}
    </div>
  );
}
